import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import type { SystemRole } from "@adva/auth";
import { defineAbilityFor } from "@adva/auth";

import { SettingsPage } from "@/components/v2/settings-page";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PermissionsGrid } from "@/components/permissions-grid";
import { EditRoleSheet } from "@/components/settings/edit-role-sheet";
import { useCurrentUser } from "@/hooks/use-current-user";
import { useAccountUsers, type AccountUserOnAccount } from "@/hooks/use-admin";
import { useActivityEvents } from "@/hooks/use-activity-events";
import { formatRole, formatRelativeDate } from "@/lib/format-utils";

// ADV-881: detail page for a single account member. Two tabs:
//
//   - Profile: identity + role badge + last role-change card.
//   - Permissions: PermissionsGrid built from the user's resolved ability.
//
// Editing still happens via the existing <EditRoleSheet>, opened from the
// "Edit role" button in the header. This page does NOT introduce a new role
// editing surface — it just gives the matrix a destination.

// ADV-1034: source map only contains values produced by the API today —
// "owner" was dropped because AccountRoleSchema has rejected it since
// ADV-1047 and the DB CHECK constraint excludes it since ADV-1063.
const LEGACY_TO_SYSTEM_ROLE: Record<string, SystemRole> = {
  super_admin: "super_admin",
  admin: "admin",
  billing: "billing",
  viewer: "user",
};

export default function V2SettingsMemberDetailPage() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const currentUser = useCurrentUser();
  const { data, isLoading } = useAccountUsers(currentUser?.accountId);
  const [editOpen, setEditOpen] = useState<AccountUserOnAccount | null>(null);

  const member = useMemo(
    () => (userId ? data?.users.find((u) => u.id === userId) : undefined),
    [userId, data?.users],
  );

  if (isLoading) {
    return (
      <SettingsPage
        breadcrumbs={[
          { label: "Settings" },
          { label: "Account" },
          { label: "Users", href: "/settings/members" },
          { label: "—" },
        ]}
        title="Loading member…"
      >
        <Card>
          <CardContent className="py-12 text-center">
            <p className="typo-body text-[color:var(--color-text-md)]">
              Loading…
            </p>
          </CardContent>
        </Card>
      </SettingsPage>
    );
  }

  if (!member) {
    return (
      <SettingsPage
        breadcrumbs={[
          { label: "Settings" },
          { label: "Account" },
          { label: "Users", href: "/settings/members" },
          { label: "Not found" },
        ]}
        title="User not found"
      >
        <Card>
          <CardContent className="py-12 text-center space-y-3">
            <p className="typo-body text-[color:var(--color-text-md)]">
              No member with this ID is on your account.
            </p>
            <Button
              variant="outline"
              onClick={() => navigate("/settings/members")}
            >
              Back to members
            </Button>
          </CardContent>
        </Card>
      </SettingsPage>
    );
  }

  const name =
    [member.first_name, member.last_name].filter(Boolean).join(" ") ||
    member.email;

  return (
    <SettingsPage
      breadcrumbs={[
        { label: "Settings" },
        { label: "Account" },
        { label: "Users", href: "/settings/members" },
        { label: name },
      ]}
      title={name}
      subtitle={member.email}
      actions={
        <Button variant="outline" onClick={() => setEditOpen(member)}>
          Edit role
        </Button>
      }
    >
      <Tabs defaultValue="profile" className="w-full">
        <TabsList>
          <TabsTrigger value="profile">Profile</TabsTrigger>
          <TabsTrigger value="permissions">Permissions</TabsTrigger>
        </TabsList>

        <TabsContent value="profile" className="pt-6 space-y-4">
          <ProfileTab member={member} />
        </TabsContent>

        <TabsContent value="permissions" className="pt-6 space-y-4">
          <PermissionsTab
            member={member}
            accountId={member.account_id}
            businessId={currentUser?.businessId || null}
          />
        </TabsContent>
      </Tabs>

      <EditRoleSheet member={editOpen} onClose={() => setEditOpen(null)} />
    </SettingsPage>
  );
}

interface ProfileTabProps {
  member: AccountUserOnAccount;
}

function ProfileTab({ member }: ProfileTabProps) {
  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Role</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="capitalize">
              {formatRole(member.account_role)}
            </Badge>
            <Badge variant={member.status === "active" ? "default" : "outline"}>
              {member.status}
            </Badge>
          </div>
        </CardContent>
      </Card>

      <LastRoleChangeCard userId={member.id} />
    </>
  );
}

interface LastRoleChangeCardProps {
  userId: string;
}

function LastRoleChangeCard({ userId }: LastRoleChangeCardProps) {
  // ADV-881 Codex P2: filter server-side. Older versions of this card scanned
  // the unfiltered /entity/:user/:id feed and would miss the most recent role
  // change if many newer non-role events (operator notes etc.) pushed it off
  // the first page.
  const { data, isLoading } = useActivityEvents({
    entityType: "user",
    entityId: userId,
    eventType: "entity.updated",
    limit: 1,
  });

  const lastChange = useMemo(() => data?.activity_events?.[0] ?? null, [data]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Last role change</CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <p className="typo-body text-[color:var(--color-text-md)]">
            Loading…
          </p>
        ) : lastChange ? (
          <div className="space-y-1">
            <p className="typo-body text-[color:var(--color-text-hi)]">
              {formatRelativeDate(lastChange.created_at)}
            </p>
            <p className="typo-label text-[color:var(--color-text-md)]">
              by{" "}
              {lastChange.actor_type === "user"
                ? `user ${lastChange.actor_id ?? "(unknown)"}`
                : lastChange.actor_type === "api_key"
                  ? `API key ${lastChange.actor_id ?? "(unknown)"}`
                  : "system"}
            </p>
            <RoleChangeDiff metadata={lastChange.metadata} />
          </div>
        ) : (
          <p className="typo-body text-[color:var(--color-text-md)]">
            No role changes recorded yet.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function RoleChangeDiff({ metadata }: { metadata: Record<string, unknown> }) {
  const before = metadata.before as { account_role?: string } | undefined;
  const after = metadata.after as { account_role?: string } | undefined;
  if (!before?.account_role || !after?.account_role) return null;
  return (
    <p className="typo-label text-[color:var(--color-text-md)]">
      {formatRole(before.account_role)} → {formatRole(after.account_role)}
    </p>
  );
}

interface PermissionsTabProps {
  member: AccountUserOnAccount;
  accountId: string;
  businessId: string | null;
}

function PermissionsTab({
  member,
  accountId,
  businessId,
}: PermissionsTabProps) {
  const ability = useMemo(() => {
    // Map legacy account_role to the unified system_role enum. The mirror of
    // this mapping lives in use-current-user.ts; both should be deleted in
    // favour of reading system_role from the JWT once ADV-879 Phase 4 lands.
    const systemRole = LEGACY_TO_SYSTEM_ROLE[member.account_role] ?? "user";
    return defineAbilityFor({
      systemRole,
      endUserRole: systemRole === "user" ? "manager" : null,
      accountId,
      businessId,
      userId: member.id,
    });
  }, [member.account_role, member.id, accountId, businessId]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Resolved abilities</CardTitle>
      </CardHeader>
      <CardContent>
        <p className="mb-4 typo-body text-[color:var(--color-text-md)]">
          What this user can do in the active business, evaluated through CASL
          against the current account and business scope.
        </p>
        <PermissionsGrid
          ability={ability}
          scope={{ accountId, businessId, userId: member.id }}
        />
      </CardContent>
    </Card>
  );
}
